import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Users, Download, Upload, CheckCircle2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ScoreChip } from "../components/ScoreChip";
import { PhoneLink, EmailLink } from "../components/ContactLinks";

const STAGES = ["all", "new", "contacted", "nurturing", "active", "offer", "under_contract", "closed", "lost"];

export default function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [scores, setScores] = useState({});
  const [funnel, setFunnel] = useState({}); // lead_id -> {client_id, tx_count}
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (stage !== "all") params.stage = stage;
    if (q) params.search = q;
    const { data } = await api.get("/leads", { params });
    setLeads(data);
    setSelected(new Set());
    setLoading(false);
    // Funnel state (which leads are already clients) — one round-trip, cached in state.
    api.get("/leads/funnel-summary").then(r => setFunnel(r.data.leads || {})).catch(() => {});
    data.forEach(async (l) => {
      try {
        const { data: s } = await api.get(`/leads/${l.id}/score`);
        setScores(prev => ({ ...prev, [l.id]: s }));
      } catch (err) {
        // Score endpoint may 404 if the lead was archived mid-load — swallow but log.
        if (process.env.NODE_ENV !== "production") console.error("[LeadsList] score fetch failed", l.id, err);
      }
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [stage]);

  const allSelected = useMemo(() => leads.length > 0 && selected.size === leads.length, [leads, selected]);
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(leads.map(l => l.id)));
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulk = async (fn) => {
    setActing(true);
    try { await fn(); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    finally { setActing(false); }
  };

  const markContacted = () => bulk(async () => {
    const { data } = await api.post("/leads/bulk-patch", { ids: [...selected], stage: "contacted" });
    toast.success(`${data.updated} marked as contacted`);
  });

  const archiveSelected = () => {
    if (!confirm(`Archive ${selected.size} lead${selected.size > 1 ? "s" : ""}? This deletes them.`)) return;
    return bulk(async () => {
      const { data } = await api.post("/leads/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} deleted`);
    });
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/exports/leads.csv", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match?.[1] || `realtoros-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Leads</h1>
          <p className="mt-1 text-sm text-slate-500">Your pipeline, scoped to your workspace.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/leads/import"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 shadow-sm"
            data-testid="import-leads-btn"
          >
            <Upload className="h-4 w-4" /> Import Leads
          </Link>
          <Link to="/leads/new" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm" style={{ background: "var(--primary-hex)" }} data-testid="add-lead-btn">
            <Plus className="h-4 w-4" /> Add lead
          </Link>
        </div>
      </div>
      <div className="mb-4 -mt-2 flex items-center gap-4 flex-wrap gap-y-2">
        <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900" data-testid="export-leads-btn">
          <Download className="h-3.5 w-3.5" /> Export all leads to CSV
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search name, email, phone…" className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus-brand" data-testid="leads-search-input" />
        </div>
        <select value={stage} onChange={e => setStage(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="leads-stage-filter">
          {STAGES.map(s => <option key={s} value={s}>{s === "all" ? "All stages" : s.replace("_"," ")}</option>)}
        </select>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-slate-900 bg-slate-900 text-white px-4 py-2.5" data-testid="bulk-actions-bar">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button disabled={acting} onClick={markContacted} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded" data-testid="bulk-mark-contacted">
              <CheckCircle2 className="h-3.5 w-3.5" /> Mark contacted
            </button>
            <button disabled={acting} onClick={exportCsv} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded" data-testid="bulk-export">
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button disabled={acting} onClick={archiveSelected} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-rose-500 hover:bg-rose-600 px-3 py-1.5 rounded" data-testid="bulk-delete">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
            <button onClick={() => setSelected(new Set())} className="p-1.5 rounded hover:bg-white/10" data-testid="bulk-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="pl-6 pr-2 py-3 font-medium">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" data-testid="select-all-checkbox" />
                </th>
                <th className="px-6 py-3 font-medium">Lead</th>
                <th className="px-6 py-3 font-medium">Stage</th>
                <th className="px-6 py-3 font-medium">Score</th>
                <th className="px-6 py-3 font-medium">Consent</th>
                <th className="px-6 py-3 font-medium">Last contact</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="px-6 py-8 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-16 text-center" data-testid="leads-empty">
                  <Users className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="mt-3 font-heading text-lg font-semibold text-slate-900">No leads yet</div>
                  <p className="text-slate-500 mt-1 max-w-md mx-auto text-sm">Add a lead manually, import a CSV from your old CRM, or wait for your first client-portal signup to land here.</p>
                  <div className="mt-5 flex items-center justify-center gap-3">
                    <Link to="/leads/new" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }}>
                      <Plus className="h-4 w-4" /> Add your first lead
                    </Link>
                    <Link to="/leads/import" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200">
                      Import CSV
                    </Link>
                  </div>
                </td></tr>
              )}
              {leads.map((l) => {
                const sc = scores[l.id];
                const isSel = selected.has(l.id);
                return (
                  <tr key={l.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-slate-50" : ""}`} data-testid={`lead-row-${l.id}`}>
                    <td className="pl-6 pr-2 py-4">
                      <input type="checkbox" checked={isSel} onChange={() => toggle(l.id)} className="rounded" data-testid={`select-lead-${l.id}`} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/leads/${l.id}`} className="font-semibold text-slate-900 hover:underline">{l.full_name}</Link>
                        {funnel[l.id] && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                            title={`Client · ${funnel[l.id].tx_count} transaction${funnel[l.id].tx_count === 1 ? "" : "s"}`}
                            data-testid={`lead-client-badge-${l.id}`}
                          >
                            → Client{funnel[l.id].tx_count ? ` · ${funnel[l.id].tx_count} tx` : ""}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {l.phone && <PhoneLink phone={l.phone} testId={`lead-phone-${l.id}`} />}
                        {l.email && <EmailLink email={l.email} testId={`lead-email-${l.id}`} />}
                        {!l.phone && !l.email && <span>—</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4"><span className="text-xs uppercase tracking-wider text-slate-600">{l.stage.replace("_", " ")}</span></td>
                    <td className="px-6 py-4">{sc ? <ScoreChip temperature={sc.temperature} score={sc.score} /> : <span className="text-xs text-slate-400">…</span>}</td>
                    <td className="px-6 py-4 text-xs text-slate-600">
                      {l.do_not_contact ? <span className="text-red-700 font-semibold">DNC</span> : (
                        <>
                          {l.sms_consent && <span className="mr-2">SMS</span>}
                          {l.email_consent && <span>Email</span>}
                          {!l.sms_consent && !l.email_consent && <span className="text-slate-400">none</span>}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-600">{l.last_contacted_at ? new Date(l.last_contacted_at).toLocaleDateString() : "never"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden">
          {loading && <div className="px-6 py-8 text-center text-slate-500">Loading…</div>}
          {!loading && leads.length === 0 && (
            <div className="px-6 py-16 text-center" data-testid="leads-empty-mobile">
              <Users className="h-10 w-10 mx-auto text-slate-300" />
              <div className="mt-3 font-heading text-lg font-semibold text-slate-900">No leads yet</div>
              <Link to="/leads/new" className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }}>
                <Plus className="h-4 w-4" /> Add your first lead
              </Link>
            </div>
          )}
          {leads.map(l => {
            const sc = scores[l.id];
            const isSel = selected.has(l.id);
            return (
              <div key={l.id} className={`px-4 py-3 border-b border-slate-100 flex gap-3 ${isSel ? "bg-slate-50" : ""}`}>
                <input type="checkbox" checked={isSel} onChange={() => toggle(l.id)} className="mt-1 rounded" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/leads/${l.id}`} className="font-semibold text-slate-900 hover:underline truncate">{l.full_name}</Link>
                    {sc && <ScoreChip temperature={sc.temperature} score={sc.score} />}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {l.phone && <PhoneLink phone={l.phone} testId={`lead-mobile-phone-${l.id}`} />}
                    {l.email && <EmailLink email={l.email} testId={`lead-mobile-email-${l.id}`} />}
                    {!l.phone && !l.email && <span className="text-xs text-slate-500">—</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{l.stage.replace("_", " ")}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
